import React, { useState, useEffect } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const fmt = (d) => {
  if (!d) return null;
  const date = new Date(d);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const QUICK_TEMPLATES = [
  'Call vet', 'Schedule farrier', 'Order fly spray', 'Check blanket fit',
  'Ask trainer about bit', 'Refill medication', 'Order hay', 'Renew Coggins',
  'Book bodywork', 'Submit show entry',
];

const RemindersPage = () => {
  const [reminders, setReminders] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', horse_id: '', due_date: '', due_time: '', is_pinned: false, repeat_type: 'none', notes: '' });

  const load = async () => {
    try {
      const [rRes, hRes] = await Promise.all([api.get('/reminders'), api.get('/horses')]);
      setReminders(rRes.data);
      setHorses(hRes.data);
    } catch { toast.error('Failed to load reminders'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleComplete = async (id) => {
    try {
      await api.put(`/reminders/${id}/complete`);
      setReminders(p => p.filter(r => r.id !== id));
      toast.success('Done!');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      setReminders(p => p.filter(r => r.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const handleTogglePin = async (r) => {
    try {
      await api.put(`/reminders/${r.id}`, { is_pinned: !r.is_pinned });
      setReminders(p => p.map(rem => rem.id === r.id ? { ...rem, is_pinned: !rem.is_pinned } : rem));
    } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Reminder text required');
    try {
      let due_date = null;
      if (form.due_date) {
        due_date = form.due_time ? new Date(`${form.due_date}T${form.due_time}`).toISOString() : new Date(`${form.due_date}T08:00`).toISOString();
      }
      const { data } = await api.post('/reminders', { ...form, due_date, horse_id: form.horse_id || null });
      setReminders(p => [data, ...p]);
      setShowAdd(false);
      setForm({ title: '', horse_id: '', due_date: '', due_time: '', is_pinned: false, repeat_type: 'none', notes: '' });
      toast.success('Reminder added!');
    } catch { toast.error('Failed to save'); }
  };

  const pinned = reminders.filter(r => r.is_pinned);
  const upcoming = reminders.filter(r => !r.is_pinned && r.due_date);
  const noDate = reminders.filter(r => !r.is_pinned && !r.due_date);

  const ReminderItem = ({ r }) => (
    <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-color)', alignItems: 'flex-start' }}>
      <button onClick={() => handleComplete(r.id)}
        style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border-strong)', background: 'none', cursor: 'pointer', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{r.title}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
          {r.due_date && <span style={{ fontSize: 11, color: 'var(--cr-amber)' }}>📅 {fmt(r.due_date)}</span>}
          {r.horse && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>🐴 {r.horse.name}</span>}
          {r.is_pinned && <span className="badge badge-green" style={{ fontSize: 10 }}>Pinned</span>}
        </div>
        {r.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{r.notes}</div>}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button onClick={() => handleTogglePin(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, opacity: r.is_pinned ? 1 : 0.4 }}>📌</button>
        <button onClick={() => handleDelete(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>×</button>
      </div>
    </div>
  );

  return (
    <div>
      <TopBar title="Reminders" subtitle="Pinned notes & to-dos"
        rightAction={<button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }} onClick={() => setShowAdd(true)}>+ Add</button>}
      />

      <div className="page-content">
        {loading && <div className="loading-screen"><div className="spinner" /></div>}

        {!loading && reminders.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🔔</div>
            <h3>No reminders</h3>
            <p>Add anything you need to remember — with or without a due date</p>
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>Add reminder</button>
          </div>
        )}

        {pinned.length > 0 && (
          <>
            <div className="section-label">📌 Pinned</div>
            <div className="card" style={{ padding: '0 14px' }}>
              {pinned.map(r => <ReminderItem key={r.id} r={r} />)}
            </div>
          </>
        )}

        {upcoming.length > 0 && (
          <>
            <div className="section-label">📅 Scheduled</div>
            <div className="card" style={{ padding: '0 14px' }}>
              {upcoming.sort((a, b) => new Date(a.due_date) - new Date(b.due_date)).map(r => <ReminderItem key={r.id} r={r} />)}
            </div>
          </>
        )}

        {noDate.length > 0 && (
          <>
            <div className="section-label">No due date</div>
            <div className="card" style={{ padding: '0 14px' }}>
              {noDate.map(r => <ReminderItem key={r.id} r={r} />)}
            </div>
          </>
        )}

        {!loading && reminders.length > 0 && (
          <button className="btn btn-secondary btn-full" style={{ marginTop: 8 }} onClick={() => setShowAdd(true)}>+ Add another reminder</button>
        )}
      </div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2>Add reminder</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            <div className="form-group">
              <label className="form-label">What do you need to remember? *</label>
              <textarea className="form-input" rows={2} placeholder="e.g. Call vet about left front, ask coach about bit change..." value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
            </div>

            <div className="section-label" style={{ margin: '0 0 8px' }}>Quick add</div>
            <div className="chip-group" style={{ marginBottom: 14 }}>
              {QUICK_TEMPLATES.map(t => (
                <div key={t} className="chip" onClick={() => setForm(p => ({ ...p, title: t }))} style={{ fontSize: 11 }}>{t}</div>
              ))}
            </div>

            {horses.length > 0 && (
              <div className="form-group">
                <label className="form-label">Horse (optional)</label>
                <div className="chip-group">
                  <div className={`chip ${!form.horse_id ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, horse_id: '' }))}>Any</div>
                  {horses.map(h => <div key={h.id} className={`chip ${form.horse_id === h.id ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, horse_id: h.id }))}>{h.name}</div>)}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Due date (optional)</label>
                <input className="form-input" type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
              {form.due_date && (
                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input className="form-input" type="time" value={form.due_time} onChange={e => setForm(p => ({ ...p, due_time: e.target.value }))} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_pinned} onChange={e => setForm(p => ({ ...p, is_pinned: e.target.checked }))} style={{ accentColor: 'var(--cr-green)' }} />
                📌 Pin to dashboard
              </label>
            </div>

            <button className="btn btn-primary btn-full" onClick={handleSave}>Save reminder</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersPage;
